<script setup lang="ts">
  import type { ProductVariantPickerResource, ProductVariantSimpleResource } from '@storefront/resources';
  import { onMounted, ref, type Ref, nextTick } from 'vue';
  import { useHttpClient, useRoute } from '@othercommerce/foundation';

  interface AttributeOption {
    label: string,
    value: number,
  }

  interface Attribute {
    label: string,
    options: AttributeOption[],
    value: number,
  }

  interface Props {
    variant: ProductVariantPickerResource,
  }

  interface Emits {
    (e: 'selected', variant: ProductVariantSimpleResource): void,
  }

  const emit = defineEmits<Emits>();
  const props = defineProps<Props>();
  const http = useHttpClient();
  const route = useRoute();

  const variant = ref(props.variant);
  const loading = ref(true);
  const attributes = ref() as Ref<Attribute[]>;
  const selected = ref() as Ref<any>;
  const variants = ref() as Ref<ProductVariantSimpleResource[]>;
  const series = ref() as Ref<ProductVariantSimpleResource[]>;

  onMounted(() => {
    let params = { product: variant.value.productId, variant: variant.value.id };

    http.dispatch('GET', route('web.product.picker.api', params)).then((response) => {
      variant.value = response.variant;
      attributes.value = response.attributes;
      selected.value = response.selected;
      variants.value = response.variants;
      series.value = response.series;

      nextTick(() => {
        loading.value = false;
      });
    });
  });

  function onSelected(variant: ProductVariantSimpleResource) {
    emit('selected', variant);
  }
</script>
<template>
  <SfDataLoader :loading="loading">
    <div v-if="series.length > 0 || attributes.length > 0 || variants.length > 0" class="variant-picker">
      <SfVariantPickerGrid v-if="series.length > 0" show="series" :variant :series @selected="onSelected">
        <template #title>
          {{ $t('SHOP.CART.LABEL.OTHERS_IN_SERIE') }}
        </template>
      </SfVariantPickerGrid>
      <SfVariantPickerAttributes v-if="attributes.length > 0" :variant :attributes :selected @selected="onSelected">
        <template #title>
          {{ $t('SHOP.CART.LABEL.SELECT_VARIANT') }}
        </template>
      </SfVariantPickerAttributes>
      <SfVariantPickerGrid v-else-if="variants.length > 0" show="variants" :variant :variants @selected="onSelected">
        <template #title>
          {{ $t('SHOP.CART.LABEL.SELECT_VARIANT') }}
        </template>
      </SfVariantPickerGrid>
    </div>
  </SfDataLoader>
</template>
<style scoped lang="scss">
  :root {
    --sf-variants-grid-gap: 3px;
    --sf-variants-grid-link-radius: 0px;
    --sf-variants-grid-link-border-color: var(--secondary-border);
    --sf-variants-grid-link-border-color-active: var(--primary-border-active);
    --sf-variants-grid-link-border-color-hover: var(--secondary-border-hover);
  }

  .variant-picker {
    display: flex;
    flex-direction: column;
    gap: var(--gap-card);
  }
</style>
